import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { CreditCard, Plus, PencilSimple, Trash, CheckCircle, X, CurrencyGbp, Clock, Users, Folder, Truck, HardDrive, EnvelopeSimple, PaperPlaneTilt, Sparkle, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import PricingConfigPanel from "../components/PricingConfigPanel";
import RevolutConnectPanel from "../components/RevolutConnectPanel";
import PaymentRecoveryPanel from "../components/PaymentRecoveryPanel";
import { ADDON_KEYS, featureLabel } from "../lib/features";
import { money } from "../lib/pricing";

const EMPTY_FORM = {
  id: "",
  name: "",
  monthly_price: "",
  annual_price: "",
  one_time_price: "",
  trial_days: 7,
  features: [""],
  limits: { users: 5, projects: -1, suppliers: -1, storage_gb: 50, ai_drafting: true, ai_daily_limit: 200 },
  apply_addons_to_tenants: true,
  kind: "subscription",
  feature_keys: [],
  included_seats: 3,
  bundle_discount_pct: 0,
  blurb: "",
};

const previewPrice = (config, form) => {
  if (!config) return null;
  const seats = Math.max(1, parseInt(form.included_seats, 10) || 1);
  const list = config.per_user_monthly * seats + (form.feature_keys || []).reduce((s, k) => s + (config.addon_prices?.[k] || 0), 0);
  const monthly = list * (1 - (parseFloat(form.bundle_discount_pct) || 0) / 100);
  return { list, monthly, annual: monthly * 12 * (1 - config.annual_discount_pct / 100) };
};

export default function SubscriptionPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deletePlan, setDeletePlan] = useState(null);
  const [tenantCounts, setTenantCounts] = useState({});
  const [trialNotifications, setTrialNotifications] = useState([]);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [config, setConfig] = useState(null);

  const loadCatalog = useCallback(() => API.get("/billing/catalog").then(({ data }) => setConfig({
    per_user_monthly: data.config.per_user_monthly, annual_discount_pct: data.config.annual_discount_pct,
    addon_prices: Object.fromEntries(data.addons.map((a) => [a.key, a.monthly_price])), sellable: data.addons.filter((a) => a.sellable !== false).map((a) => a.key),
  })).catch(() => {}), []);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await API.get("/subscription-plans");
      setPlans(data.plans || []);
      const counts = {};
      (data.plans || []).forEach(p => { counts[p.id] = p.tenant_count || 0; });
      setTenantCounts(counts);
    } catch {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    loadCatalog();
    API.get("/trial-notifications").then(({ data }) => setTrialNotifications(data.notifications || [])).catch(() => {});
  }, [fetchPlans, loadCatalog]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, features: [""] });
    setEditPlan(null);
    setShowCreate(true);
  };

  const openEdit = (plan) => {
    setForm({
      id: plan.id,
      name: plan.name,
      monthly_price: plan.monthly_price ?? "",
      annual_price: plan.annual_price ?? "",
      one_time_price: plan.one_time_price ?? "",
      trial_days: plan.trial_days ?? 7,
      features: plan.features?.length ? [...plan.features] : [""],
      limits: { ...EMPTY_FORM.limits, ...plan.limits },
      apply_addons_to_tenants: true,
      kind: plan.kind || (plan.one_time_price && !plan.monthly_price ? "one_time" : "subscription"),
      feature_keys: plan.feature_keys || [],
      included_seats: plan.included_seats ?? 3,
      bundle_discount_pct: plan.bundle_discount_pct ?? 0,
      blurb: plan.blurb || "",
    });
    setEditPlan(plan);
    setShowCreate(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const features = form.features.filter(f => f.trim());
      const payload = {
        name: form.name,
        monthly_price: form.monthly_price !== "" ? parseFloat(form.monthly_price) : null,
        annual_price: form.annual_price !== "" ? parseFloat(form.annual_price) : null,
        one_time_price: form.one_time_price !== "" ? parseFloat(form.one_time_price) : null,
        trial_days: parseInt(form.trial_days) || 0,
        features,
        limits: {
          users: form.kind === "one_time" ? -1 : (parseInt(form.included_seats) || 1),
          projects: parseInt(form.limits.projects) || -1,
          suppliers: parseInt(form.limits.suppliers) || -1,
          storage_gb: parseInt(form.limits.storage_gb) || -1,
          ai_drafting: form.feature_keys.includes("ai_drafting"),
          ai_daily_limit: Math.max(0, parseInt(form.limits.ai_daily_limit) || 0),
        },
        kind: form.kind,
        feature_keys: form.kind === "one_time" ? ADDON_KEYS : form.feature_keys,
        included_seats: form.kind === "one_time" ? -1 : (parseInt(form.included_seats) || 1),
        bundle_discount_pct: parseFloat(form.bundle_discount_pct) || 0,
        blurb: form.blurb,
      };

      if (editPlan) {
        const { data } = await API.put(`/subscription-plans/${editPlan.id}`, { ...payload, apply_addons_to_tenants: !!form.apply_addons_to_tenants });
        const n = data?.tenants_updated || 0;
        toast.success(n ? `Plan "${form.name}" updated — seats & add-ons applied to ${n} tenant${n === 1 ? "" : "s"}` : `Plan "${form.name}" updated`);
      } else {
        payload.id = form.id || form.name.toLowerCase().replace(/\s+/g, "_");
        await API.post("/subscription-plans", payload);
        toast.success(`Plan "${form.name}" created`);
      }
      setShowCreate(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePlan) return;
    try {
      await API.delete(`/subscription-plans/${deletePlan.id}`);
      toast.success(`Plan "${deletePlan.name}" deleted`);
      setDeletePlan(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete plan");
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await API.put(`/subscription-plans/${plan.id}`, { active: !plan.active });
      toast.success(`Plan "${plan.name}" ${plan.active ? "deactivated" : "activated"}`);
      fetchPlans();
    } catch {
      toast.error("Failed to update plan");
    }
  };

  const addFeature = () => setForm(prev => ({ ...prev, features: [...prev.features, ""] }));
  const removeFeature = (idx) => setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  const updateFeature = (idx, val) => setForm(prev => {
    const features = [...prev.features];
    features[idx] = val;
    return { ...prev, features };
  });

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading plans...</div>;

  return (
    <div data-testid="subscription-plans-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
            <CreditCard size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Subscription Plans</h1>
            <p className="text-xs text-muted-foreground">Create and manage billing plans for your tenants</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="create-plan-btn">
          <Plus size={16} className="mr-1" /> Create Plan
        </Button>
      </div>

      <RevolutConnectPanel />

      <PricingConfigPanel onSaved={() => { loadCatalog(); fetchPlans(); }} />

      {/* Plans Grid */}
      <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1"><Tag size={12} /> BUNDLES — priced from the matrix above (seats × per-user + add-ons, minus bundle discount)</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map(plan => (
          <div
            key={plan.id}
            className={`border rounded-lg overflow-hidden transition-all ${plan.active ? "border-border bg-card" : "border-dashed border-muted-foreground/30 bg-muted/20 opacity-70"}`}
            data-testid={`plan-card-${plan.id}`}
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold font-[Chivo]">{plan.name}</h3>
                    {!plan.active && <Badge variant="secondary" className="text-[8px]">Inactive</Badge>}
                  </div>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{plan.id}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeletePlan(plan)} data-testid={`delete-plan-${plan.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>

              {/* Pricing */}
              <div className="flex items-baseline gap-2 mb-3">
                {plan.kind !== "one_time" && plan.computed ? (
                  <>
                    <span className="text-2xl font-bold font-[Chivo]" data-testid={`plan-computed-price-${plan.id}`}>{money(plan.computed.monthly_total, { compact: true })}</span>
                    <span className="text-xs text-muted-foreground">/month for {plan.included_seats} users{plan.bundle_discount_pct > 0 && <span className="text-emerald-600 font-semibold"> · −{plan.bundle_discount_pct}% bundle</span>}</span>
                  </>
                ) : plan.one_time_price != null ? (
                  <>
                    <span className="text-2xl font-bold font-[Chivo]">
                      <CurrencyGbp size={16} className="inline -mt-1" weight="bold" />{plan.one_time_price}
                    </span>
                    <span className="text-xs text-muted-foreground">one-time</span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">No price set</span>
                )}
              </div>

              {/* Trial */}
              <div className="flex items-center gap-1.5 mb-3 text-xs text-muted-foreground">
                <Clock size={12} />
                <span>{plan.trial_days > 0 ? `${plan.trial_days}-day free trial` : "No trial"}</span>
              </div>

              {/* Tenants count */}
              <div className="flex items-center gap-1.5 mb-3 text-xs">
                <Users size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">{tenantCounts[plan.id] || 0} tenant(s) on this plan</span>
              </div>

              {/* Add-ons in this bundle */}
              <div className="flex flex-wrap gap-1 mb-3" data-testid={`plan-addons-${plan.id}`}>
                {(plan.feature_keys || []).length === 0 && <span className="text-[10px] text-muted-foreground">Core modules only</span>}
                {(plan.feature_keys || []).map((k) => <Badge key={k} variant="secondary" className="text-[9px] font-normal">{featureLabel(k)}</Badge>)}
              </div>
              {/* Limits */}
              <div className="grid grid-cols-2 gap-2 mb-3 text-[10px]">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Users size={10} /> {plan.included_seats === -1 ? "Unlimited" : plan.included_seats} Users included
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Folder size={10} /> {plan.limits?.projects === -1 ? "Unlimited" : plan.limits?.projects} Projects
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Truck size={10} /> {plan.limits?.suppliers === -1 ? "Unlimited" : plan.limits?.suppliers} Suppliers
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <HardDrive size={10} /> {plan.limits?.storage_gb === -1 ? "Unlimited" : `${plan.limits?.storage_gb}GB`} Storage
                </div>
              </div>

              {/* Features */}
              {plan.features?.length > 0 && (
                <div className="space-y-1 mb-3">
                  {plan.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                      <CheckCircle size={12} className="text-emerald-500 shrink-0 mt-0.5" weight="fill" />
                      {f}
                    </div>
                  ))}
                </div>
              )}

              {/* Active Toggle */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">Active</span>
                <span className={`flex items-center gap-1 text-[10px] ${(plan.feature_keys || []).includes("ai_drafting") ? "text-violet-600 dark:text-violet-400" : "text-muted-foreground line-through"}`} data-testid={`plan-ai-badge-${plan.id}`}>
                  <Sparkle size={10} weight="fill" /> AI drafting{(plan.feature_keys || []).includes("ai_drafting") ? ` · ${plan.limits?.ai_daily_limit ? `${plan.limits.ai_daily_limit}/day` : "unlimited"}` : ""}
                </span>
                <Switch checked={plan.active} onCheckedChange={() => handleToggleActive(plan)} data-testid={`toggle-active-${plan.id}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <p className="text-muted-foreground mb-2">No subscription plans yet</p>
          <Button variant="outline" size="sm" onClick={openCreate}>Create your first plan</Button>
        </div>
      )}

      {/* Trial Expiry Notifications */}
      <div className="border border-border rounded-lg overflow-hidden mt-6" data-testid="trial-notifications-section">
        <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={14} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold font-[Chivo]">Trial Expiry Notifications</h3>
            <Badge variant="secondary" className="text-[9px]">{trialNotifications.length} sent</Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            disabled={sendingNotifications}
            onClick={async () => {
              setSendingNotifications(true);
              try {
                const { data } = await API.post("/trial-notifications/send-now");
                toast.success(`Sent ${data.sent} notification(s) to ${data.tenants_checked} trialing tenant(s)`);
                const { data: notifs } = await API.get("/trial-notifications");
                setTrialNotifications(notifs.notifications || []);
              } catch { toast.error("Failed to send"); }
              finally { setSendingNotifications(false); }
            }}
            data-testid="send-trial-notifications-btn"
          >
            <PaperPlaneTilt size={12} className="mr-1" />
            {sendingNotifications ? "Sending..." : "Send Now"}
          </Button>
        </div>
        <p className="px-4 py-2 text-[10px] text-muted-foreground bg-muted/10 border-b border-border">
          Automatic emails are sent every 12 hours to tenants whose trial expires within 2 days. Use "Send Now" to trigger manually.
        </p>
        {trialNotifications.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center">No trial notifications sent yet</p>
        ) : (
          <div className="divide-y divide-border max-h-[300px] overflow-y-auto">
            {trialNotifications.map(n => (
              <div key={n.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`trial-notif-${n.id}`}>
                <div>
                  <p className="text-xs font-medium">{n.tenant_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {n.recipients?.join(", ")} — {n.days_remaining} day{n.days_remaining !== 1 ? "s" : ""} remaining
                  </p>
                </div>
                <div className="text-right flex items-center gap-2">
                  {n.manual && <Badge variant="outline" className="text-[8px]">Manual</Badge>}
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(n.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PaymentRecoveryPanel />

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plan-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editPlan ? "Edit Plan" : "Create New Plan"}</DialogTitle>
            <DialogDescription>{editPlan ? "Update this subscription plan." : "Define a new subscription plan with pricing and limits."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plan Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Premium" data-testid="plan-name-input" />
              </div>
              <div>
                <Label>Plan ID {editPlan && <span className="text-muted-foreground">(read-only)</span>}</Label>
                <Input
                  value={editPlan ? form.id : (form.id || form.name.toLowerCase().replace(/\s+/g, "_"))}
                  onChange={e => !editPlan && setForm({ ...form, id: e.target.value })}
                  disabled={!!editPlan}
                  placeholder="auto-generated"
                  className="font-mono text-xs"
                  data-testid="plan-id-input"
                />
              </div>
            </div>

            {/* Plan type */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-fit" data-testid="plan-kind-toggle">
              {[["subscription", "Subscription bundle"], ["one_time", "One-time licence"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${form.kind === k ? "bg-background shadow" : "text-muted-foreground"}`} data-testid={`plan-kind-${k}`}>{label}</button>
              ))}
            </div>

            <div>
              <Label>Short description</Label>
              <Input value={form.blurb} onChange={e => setForm({ ...form, blurb: e.target.value })} placeholder="Who is this bundle for?" data-testid="plan-blurb-input" />
            </div>

            {form.kind === "one_time" ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">ONE-TIME PRICE</p>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label>Licence price (£)</Label>
                    <Input type="number" step="0.01" min="0" value={form.one_time_price} onChange={e => setForm({ ...form, one_time_price: e.target.value })} placeholder="999.00" data-testid="plan-onetime-price" />
                  </div>
                  <p className="text-[10px] text-muted-foreground pb-2">Licence holders get every add-on and unlimited seats. Use the Active switch on the card to show or hide it on the public pricing page.</p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border p-3 space-y-3" data-testid="plan-matrix">
                <p className="text-xs font-semibold text-muted-foreground">BUNDLE MATRIX</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1"><Users size={12} /> Included seats</Label>
                    <Input type="number" min="1" value={form.included_seats} onChange={e => setForm({ ...form, included_seats: e.target.value })} data-testid="plan-included-seats" />
                  </div>
                  <div>
                    <Label>Bundle discount (%)</Label>
                    <Input type="number" min="0" max="90" step="1" value={form.bundle_discount_pct} onChange={e => setForm({ ...form, bundle_discount_pct: e.target.value })} data-testid="plan-bundle-discount" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Add-ons included</Label>
                  <div className="grid grid-cols-2 gap-1.5" data-testid="plan-feature-matrix">
                    {ADDON_KEYS.map(k => {
                      const on = form.feature_keys.includes(k);
                      const sellable = !config || config.sellable.includes(k);
                      return (
                        <label key={k} className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-xs cursor-pointer ${on ? "border-primary/40 bg-primary/5" : "border-border"} ${sellable ? "" : "opacity-60"}`} data-testid={`plan-feature-${k}`}>
                          <span className="flex items-center gap-2"><input type="checkbox" checked={on} onChange={e => setForm({ ...form, feature_keys: e.target.checked ? [...form.feature_keys, k] : form.feature_keys.filter(x => x !== k) })} data-testid={`plan-feature-check-${k}`} /> {featureLabel(k)}</span>
                          <span className="text-[10px] text-muted-foreground">{config ? money(config.addon_prices[k], { compact: true }) : ""}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                {(() => { const pv = previewPrice(config, form); return pv ? (
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-md bg-muted/40 px-3 py-2" data-testid="plan-price-preview">
                    <span className="text-sm font-bold font-[Chivo]">{money(pv.monthly)}<span className="text-xs text-muted-foreground font-normal">/mo</span></span>
                    {pv.list !== pv.monthly && <span className="text-[10px] text-muted-foreground line-through">{money(pv.list)} à la carte</span>}
                    <span className="text-[10px] text-muted-foreground">{money(pv.annual)}/yr billed annually · extra seats {money(config.per_user_monthly, { compact: true })}/user</span>
                  </div>
                ) : null; })()}
              </div>
            )}

            {/* Trial */}
            <div>
              <Label>Free Trial (days)</Label>
              <Input type="number" min="0" max="365" value={form.trial_days} onChange={e => setForm({ ...form, trial_days: e.target.value })} data-testid="plan-trial-days" />
              <p className="text-[10px] text-muted-foreground mt-1">Set to 0 for no trial. New signups on this plan will get this many days free.</p>
            </div>

            {/* AI quota + rollout */}
            <div className="rounded-lg border border-violet-200 dark:border-violet-900/60 bg-violet-50/60 dark:bg-violet-950/20 p-3 space-y-3" data-testid="plan-ai-addon">
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label className="text-xs flex items-center gap-1 text-violet-700 dark:text-violet-300"><Sparkle size={12} weight="fill" /> AI daily drafts per tenant</Label>
                  <Input type="number" min="0" value={form.limits.ai_daily_limit} onChange={e => setForm({ ...form, limits: { ...form.limits, ai_daily_limit: e.target.value } })} data-testid="plan-ai-daily-limit" />
                </div>
                <p className="text-[10px] text-muted-foreground pb-2">0 = unlimited. Only applies when the AI Drafting add-on is included above.</p>
              </div>
              {editPlan && (
                <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                  <Switch checked={!!form.apply_addons_to_tenants} onCheckedChange={v => setForm({ ...form, apply_addons_to_tenants: v })} data-testid="plan-ai-apply-toggle" />
                  Apply seats & add-ons to the {editPlan.tenant_count ?? 0} tenant{(editPlan.tenant_count ?? 0) === 1 ? "" : "s"} on this plan (tenants with a live Stripe subscription are left alone)
                </label>
              )}
            </div>

            {/* Features */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground">MARKETING BULLETS <span className="font-normal">(shown on pricing cards)</span></p>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px]" onClick={addFeature} data-testid="add-feature-btn">
                  <Plus size={10} className="mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {form.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-emerald-500 shrink-0" weight="fill" />
                    <Input
                      value={f}
                      onChange={e => updateFeature(i, e.target.value)}
                      placeholder={`Feature ${i + 1}`}
                      className="text-xs h-8"
                      data-testid={`plan-feature-${i}`}
                    />
                    {form.features.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeFeature(i)}>
                        <X size={12} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-plan-btn">
                {saving ? "Saving..." : editPlan ? "Update Plan" : "Create Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePlan} onOpenChange={(open) => !open && setDeletePlan(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] text-destructive">Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold text-foreground">{deletePlan?.name}</span>?
              {(tenantCounts[deletePlan?.id] || 0) > 0 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  {tenantCounts[deletePlan?.id]} tenant(s) are currently on this plan. You must move them first or deactivate the plan instead.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePlan(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-plan-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
